import React, { useState, useEffect } from 'react';
import { FaTimes, FaCalendarAlt, FaUser, FaEnvelope, FaPhoneAlt, FaComment, FaWhatsapp, FaArrowRight, FaChevronDown, FaBed } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../css/BookingModal.css';

const BookingModal = ({ onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        checkIn: null,
        checkOut: null,
        message: '',
        roomType: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [focusedField, setFocusedField] = useState(null);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (date, dateType) => {
        setFormData(prev => ({ ...prev, [dateType]: date }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const { name, email, phone, checkIn, checkOut, message, roomType } = formData;
        
        if (!name || !email || !phone || !roomType) {
            const errorElements = document.querySelectorAll('.required-field');
            errorElements.forEach(el => {
                el.classList.add('error-shake');
                setTimeout(() => el.classList.remove('error-shake'), 500);
            });
            return;
        }

        setIsSubmitting(true);

        const formattedCheckIn = checkIn ? checkIn.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) : 'Flexible';
        const formattedCheckOut = checkOut ? checkOut.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) : 'Flexible';

        const roomTypes = {
            premium: '🏰 Premium Suite',
            deluxe: '✨ Deluxe Room',
            executive: '💼 Executive Suite'
        };

        const whatsappText = `🏠 *BOOKING INQUIRY* 🏠%0A%0A━━━━━━━━━━━━━━━━━━━━━━%0A✨ *GUEST DETAILS* ✨%0A━━━━━━━━━━━━━━━━━━━━━━%0A👤 *Name:* ${name}%0A📧 *Email:* ${email}%0A📱 *Phone:* ${phone}%0A🏠 *Room Type:* ${roomTypes[roomType]}%0A%0A━━━━━━━━━━━━━━━━━━━━━━%0A📅 *STAY DETAILS* 📅%0A━━━━━━━━━━━━━━━━━━━━━━%0A🗓️ *Check-In:* ${formattedCheckIn}%0A🗓️ *Check-Out:* ${formattedCheckOut}%0A%0A━━━━━━━━━━━━━━━━━━━━━━%0A💭 *SPECIAL REQUESTS* 💭%0A━━━━━━━━━━━━━━━━━━━━━━%0A${message || 'No special requests'}`;

        const whatsappLink = `https://wa.me/918879779777?text=${encodeURIComponent(whatsappText)}`;
        
        setTimeout(() => {
            window.open(whatsappLink, '_blank');
            setIsSubmitting(false);
            onClose();
        }, 500);
    };

    const roomOptions = [
        { id: 'premium', name: 'Premium Suite', price: '₹25,000', desc: 'Luxury living with premium amenities' },
        { id: 'deluxe', name: 'Deluxe Room', price: '₹18,000', desc: 'Comfortable & stylish living space' },
        { id: 'executive', name: 'Executive Suite', price: '₹32,000', desc: 'Executive comfort with workspace' }
    ];

    return (
        <div className="booking-modal-backdrop" onClick={onClose}>
            <div className="booking-modal-wrapper">
                <div className="booking-modal-container" onClick={(e) => e.stopPropagation()}>
                    <button className="booking-modal-close" onClick={onClose}>
                        <FaTimes />
                    </button>

                    <div className="booking-modal-header">
                        <div className="booking-header-icon">
                            <FaBed className="header-icon" />
                        </div>
                        <h2>Book Your Stay</h2>
                        <p>Fill in your details to confirm your booking</p>
                    </div>

                    <form onSubmit={handleSubmit} className="booking-form">
                        {/* Name Field */}
                        <div className="form-group">
                            <div className={`input-wrapper ${focusedField === 'name' ? 'focused' : ''}`}>
                                <FaUser className="input-icon" />
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Full Name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    onFocus={() => setFocusedField('name')}
                                    onBlur={() => setFocusedField(null)}
                                    required
                                    className="required-field"
                                />
                            </div>
                        </div>

                        {/* Email and Phone Row */}
                        <div className="form-row">
                            <div className="form-group">
                                <div className={`input-wrapper ${focusedField === 'email' ? 'focused' : ''}`}>
                                    <FaEnvelope className="input-icon" />
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Email Address"
                                        value={formData.email}
                                        onChange={handleChange}
                                        onFocus={() => setFocusedField('email')}
                                        onBlur={() => setFocusedField(null)}
                                        required
                                        className="required-field"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <div className={`input-wrapper ${focusedField === 'phone' ? 'focused' : ''}`}>
                                    <FaPhoneAlt className="input-icon" />
                                    <input
                                        type="tel"
                                        name="phone"
                                        placeholder="WhatsApp Number"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        onFocus={() => setFocusedField('phone')}
                                        onBlur={() => setFocusedField(null)}
                                        required
                                        className="required-field"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Room Type Dropdown */}
                        <div className="form-group">
                            <div className="select-wrapper">
                                <FaBed className="select-icon-left" />
                                <select
                                    name="roomType"
                                    value={formData.roomType}
                                    onChange={handleChange}
                                    className="select-input"
                                    required
                                >
                                    <option value="" disabled>Select Room Type</option>
                                    {roomOptions.map((room) => (
                                        <option key={room.id} value={room.id}>
                                            {room.name}
                                        </option>
                                    ))}
                                </select>
                                <FaChevronDown className="select-icon-right" />
                            </div>
                        </div>

                        {/* Check-in and Check-out Row */}
                        <div className="form-row">
                            <div className="form-group">
                                <div className="date-wrapper">
                                    <FaCalendarAlt className="date-icon" />
                                    <DatePicker
                                        selected={formData.checkIn}
                                        onChange={(date) => handleDateChange(date, 'checkIn')}
                                        placeholderText="Check In Date"
                                        className="date-input"
                                        minDate={new Date()}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <div className="date-wrapper">
                                    <FaCalendarAlt className="date-icon" />
                                    <DatePicker
                                        selected={formData.checkOut}
                                        onChange={(date) => handleDateChange(date, 'checkOut')}
                                        placeholderText="Check Out Date"
                                        className="date-input"
                                        minDate={formData.checkIn || new Date()}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Message Field */}
                        <div className="form-group">
                            <div className={`input-wrapper textarea-wrapper ${focusedField === 'message' ? 'focused' : ''}`}>
                                <FaComment className="input-icon" />
                                <textarea
                                    name="message"
                                    placeholder="Special Requests (Optional)"
                                    value={formData.message}
                                    onChange={handleChange}
                                    onFocus={() => setFocusedField('message')}
                                    onBlur={() => setFocusedField(null)}
                                    rows="3"
                                />
                            </div>
                        </div>

                        <button 
                            type="submit" 
                            className="submit-btn"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <div className="btn-loader">
                                    <div className="loader-spinner"></div>
                                    <span>Sending...</span>
                                </div>
                            ) : (
                                <>
                                    <FaWhatsapp className="btn-whatsapp-icon" />
                                    <span>Book via WhatsApp</span>
                                    <FaArrowRight className="btn-arrow-icon" />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="modal-footer">
                        <p className="footer-note">
                            By continuing, you agree to our <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookingModal;